import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { useNotifications, useMarkAllRead, useUnreadCount } from '@/hooks/useNotifications';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui';
import { NotificationItem } from './NotificationItem';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/cn';

export function NotificationsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const notifications = useNotifications();
  const markAll = useMarkAllRead();
  const count = unread.data ?? 0;

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o && count > 0) markAll.mutate();
      }}
    >
      <DropdownMenuTrigger
        className={cn(
          'relative inline-flex h-10 w-10 items-center justify-center rounded-md text-parchment-ink',
          'coarse:h-11 coarse:w-11',
          'hover:bg-parchment-dark/50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        )}
        aria-label={t('notifications.title')}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute right-1.5 top-1.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-parchment-light">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 max-w-[calc(100vw-2rem)] p-2" align="end">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-parchment-ink">{t('notifications.title')}</p>
          <Button asChild size="sm" variant="ghost" onClick={() => setOpen(false)}>
            <Link to={ROUTES.notifications}>View all</Link>
          </Button>
        </div>
        <div className="max-h-80 space-y-1.5 overflow-y-auto">
          {notifications.isPending ? (
            <p className="text-sm text-parchment-muted">{t('common.loading')}</p>
          ) : !notifications.data || notifications.data.length === 0 ? (
            <p className="text-sm text-parchment-muted">{t('notifications.empty')}</p>
          ) : (
            notifications.data.slice(0, 8).map((n) => (
              <NotificationItem key={n.id} notification={n} />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
